/**
 * Pure offline categorization core (no Worker / Dexie).
 * Used by categorization.js and scripts/smoke-categorize.js.
 */
import { SPENDING_CATEGORIES, SUBSCRIPTION_KEYWORDS } from '../data/spendingCategories';

export const KEYWORD_INDEX = new Map();
export const KEYWORD_LIST = [];

for (const [category, subcats] of Object.entries(SPENDING_CATEGORIES)) {
    for (const [subcategory, keywords] of Object.entries(subcats)) {
        for (const keyword of keywords) {
            const normalized = keyword.toLowerCase().trim();
            if (!normalized) continue;
            const entry = { category, subcategory, keyword: normalized };
            if (!KEYWORD_INDEX.has(normalized)) {
                KEYWORD_INDEX.set(normalized, { category, subcategory });
            }
            KEYWORD_LIST.push(entry);
        }
    }
}
KEYWORD_LIST.sort((a, b) => b.keyword.length - a.keyword.length);

/** India PSP / VPA handles — never treat as merchant words */
export const PSP_SUFFIXES = new Set([
    'ybl', 'oksbi', 'okhdfcbank', 'okaxis', 'okicici', 'okbizaxis', 'okkotak',
    'paytm', 'ibl', 'apl', 'axl', 'upi', 'waaxis', 'wahdfcbank', 'wasbi',
    'pingpay', 'jupiteraxis', 'freecharge', 'fbin', 'ikwik', 'naviaxis',
    'barodampay', 'dbs', 'tapicici', 'abfspay', 'kotak', 'yesg', 'yesbank',
]);

const TYPO_MAP = [
    [/foot\s*wares?\b/g, 'footwear'],
    [/foot\s*wears?\b/g, 'footwear'],
    [/footwares?\b/g, 'footwear'],
    [/jewlery/g, 'jewellery'],
    [/jewellry/g, 'jewellery'],
    [/jewel+ers?\b/g, 'jewellery'],
    [/resturant/g, 'restaurant'],
    [/restraunt/g, 'restaurant'],
    [/restuarant/g, 'restaurant'],
    [/resta?urants?\b/g, 'restaurant'],
    [/pharamacy/g, 'pharmacy'],
    [/medicle/g, 'medical'],
    [/medicals\b/g, 'medical'],
    [/hosptial/g, 'hospital'],
    [/eletronics/g, 'electronics'],
    [/stationary\b/g, 'stationery'],
    [/stationairy/g, 'stationery'],
    [/beuty/g, 'beauty'],
    [/parler\b/g, 'parlour'],
    [/ready\s*made\b/g, 'readymade'],
    [/super\s*market\b/g, 'supermarket'],
    [/hyper\s*market\b/g, 'hypermarket'],
    [/general\s*store\b/g, 'general store'],
    [/provision\s*store\b/g, 'provision store'],
    [/kirana\s*store\b/g, 'kirana'],
    [/mobile\s*shop\b/g, 'mobile shop'],
    [/shoe\s*shop\b/g, 'shoe shop'],
    [/shoe\s*store\b/g, 'shoe store'],
    [/big\s*basket\b/g, 'bigbasket'],
    [/bb\s*now\b/g, 'bbnow'],
    [/bharat\s*bill\s*pay\b/g, 'bharat billpay'],
    [/bill\s*pay\b/g, 'billpay'],
    [/self\s*transfer\b/g, 'self transfer'],
    [/to\s*self\b/g, 'self transfer'],
];

export const COMMERCE_HINTS = [
    'shop', 'store', 'mart', 'bazaar', 'market', 'traders', 'trader', 'emporium', 'collections',
    'footwear', 'footware', 'shoes', 'shoe', 'sandal', 'sandals', 'chappal', 'garment', 'garments',
    'boutique', 'textile', 'textiles', 'tailor', 'novelty', 'readymade',
    'restaurant', 'hotel', 'cafe', 'kitchen', 'dhaba', 'biryani', 'pizza', 'burger',
    'petrol', 'diesel', 'fuel', 'pump', 'fastag', 'toll',
    'pharmacy', 'medical', 'clinic', 'hospital', 'doctor', 'dental',
    'salon', 'parlour', 'parlor', 'spa', 'barber',
    'temple', 'mandir', 'mosque', 'church', 'donation',
    'rent', 'landlord', 'pg ', ' hostel',
    'school', 'college', 'tuition', 'coaching', 'university',
    'amazon', 'flipkart', 'myntra', 'swiggy', 'zomato', 'uber', 'ola', 'rapido',
    'zepto', 'blinkit', 'instamart', 'bigbasket', 'bbnow', 'dunzo',
    'recharge', 'prepaid', 'broadband', 'electricity', 'gas bill', 'bbps', 'billpay',
    'insurance', 'premium', 'emi', 'loan', 'mutual', 'sip',
    'supermarket', 'hypermarket', 'wholesale', 'retail', 'jeweller', 'jewellery',
    'electronics', 'mobile', 'laptop', 'croma', 'decathlon',
    'gym', 'fitness', 'cult', 'paytm', 'phonepe', 'gpay',
    'pvt', 'ltd', 'llp', 'opc', 'enterprises', 'solutions', 'technologies',
    'services', 'agency', 'associates', 'industries', 'corporation', 'traders',
    'finance', 'finserv', 'bank',
];

/** Short / ambiguous tokens — never fire alone */
export const AMBIGUOUS_KEYWORDS = new Set([
    'auto', 'test', 'market', 'games', 'apple', 'gold', 'hotel', 'jio', 'angel', 'service',
    'prime', 'bar', 'rum', 'tea', 'max', 'only', 'central', 'more', 'bounce', 'vi', 'gas',
    'lab', 'cook', 'driver', 'novel', 'google', 'pay', 'cash', 'bank', 'loan', 'emi',
]);

/** Ordered disambiguation before generic keyword index */
const DISAMBIGUATION_RULES = [
    { re: /\bgold\s+loans?\b|\bloan\s+gold\b|\bgold\s+emi\b/, category: 'EMI', subcategory: 'Personal' },
    { re: /\bdigital\s+gold\b|\bsovereign\s+gold\b|\bgold\s+etf\b|\bgold\s+bond\b/, category: 'Investment', subcategory: 'Gold' },
    { re: /\bapple\s+store\b|\biphone\b|\bmacbook\b|\bipad\b|\bairpods\b/, category: 'Shopping', subcategory: 'Electronics' },
    { re: /\bapple\s+music\b|\bapple\s+tv\b|\bapple\s+one\b|\bapple\s+arcade\b|\bicloud\b/, category: 'Subscription', subcategory: 'Apple' },
    { re: /\bswiggy\s+instamart\b|\binstamart\b/, category: 'Groceries', subcategory: 'Instamart' },
    { re: /\bswiggy\s+one\b/, category: 'Subscription', subcategory: 'Others' },
    { re: /\bamazon\s+prime\b|\bprime\s+video\b|\bprime\s+membership\b/, category: 'Subscription', subcategory: 'Prime' },
    { re: /\bblood\s+test\b|\blab\s+test\b|\bdiagnostic\b/, category: 'Medical', subcategory: 'Lab Test' },
    { re: /\bangel\s+one\b/, category: 'Investment', subcategory: 'Broker' },
    { re: /\bgoogle\s+play\b|\bplay\s+store\b/, category: 'Subscription', subcategory: 'Google Play' },
    { re: /\bgoogle\s+one\b|\bgoogle\s+workspace\b|\bgoogle\s+drive\b/, category: 'Subscription', subcategory: 'Google' },
    { re: /\bgoogle\s+ads\b/, category: 'Business', subcategory: 'Marketing' },
    { re: /\bgoogle\s+pay\b|\bgpay\b/, category: 'Transfers', subcategory: 'Wallet' },
    { re: /\bphonepe\b/, category: 'Transfers', subcategory: 'Wallet' },
    { re: /\bpaytm\s+wallet\b|\bpaytm\s+postpaid\b/, category: 'Transfers', subcategory: 'Wallet' },
    { re: /\bbajaj\s+finance\b|\bbajaj\s+finserv\b/, category: 'EMI', subcategory: 'Gadget' },
    { re: /\b(?:hdfc|icici|sbi|axis|kotak|yes)\s+(?:bank\s+)?emi\b|\bloan\s+emi\b/, category: 'EMI', subcategory: 'Personal' },
    { re: /\bself\s+transfer\b|\bto\s+self\b|\bown\s+account\b/, category: 'Transfers', subcategory: 'Self transfer' },
    { re: /\bsalary\s+credit\b/, category: 'Income', subcategory: 'Salary' },
    { re: /\bcashback\b|\breward\s+credit\b/, category: 'Income', subcategory: 'Cashback' },
    { re: /\brefund\b|\breversal\b/, category: 'Income', subcategory: 'Refund' },
    { re: /\bbbps\b|\bbharat\s+billpay\b|\bbillpay\b/, category: 'Bill', subcategory: 'Electricity' },
];

function applyTypoFixes(text) {
    let out = text;
    for (const [pattern, replacement] of TYPO_MAP) {
        out = out.replace(pattern, replacement);
    }
    return out;
}

function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseAmt(amount) {
    if (typeof amount === 'number') return Math.abs(amount);
    if (!amount) return 0;
    return Math.abs(parseFloat(String(amount).replace(/[^0-9.-]/g, '')) || 0);
}

/**
 * VPA-aware normalize: keep local@psp local alphanumerics; drop PSP tokens; soft-strip long UTRs.
 */
export function normalizeMerchant(raw) {
    if (!raw) return '';
    let text = String(raw).toLowerCase().trim();

    text = text.replace(/^(upi[-/]|pos[-/]|neft[-/]|imps[-/]|ach[-/]|nach[-/])/i, '');

    const vpaMatches = text.match(/[a-z0-9._-]+@[a-z0-9.-]+/gi) || [];
    for (const vpa of vpaMatches) {
        const [local, psp] = vpa.split('@');
        const localClean = String(local || '').replace(/[^a-z0-9]/g, ' ').trim();
        const pspClean = String(psp || '').replace(/[^a-z0-9]/g, '');
        const keepLocal = localClean.length >= 2 ? localClean : '';
        text = text.replace(vpa, ` ${keepLocal} `);
        if (pspClean && PSP_SUFFIXES.has(pspClean)) {
            // already removed via replace
        }
    }

    // Strip leftover @psp tokens
    text = text.replace(/@[a-z0-9.-]+/g, ' ');
    for (const psp of PSP_SUFFIXES) {
        text = text.replace(new RegExp(`\\b${escapeRegExp(psp)}\\b`, 'g'), ' ');
    }

    text = text
        .replace(/\b\d{10,}\b/g, ' ') // UTR-like; keep shorter QR locals
        .replace(/\b(bang|blr|hyd|mum|del|che|pune|kol|ahm|ncr)\b/gi, ' ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    text = applyTypoFixes(text);
    return text.slice(0, 80);
}

export function isUnnamedQr(normalizedKey) {
    if (!normalizedKey) return true;
    const compact = normalizedKey.replace(/\s+/g, '');
    if (compact.length < 3) return true;
    if (/^q\d+$/i.test(compact)) return true;
    if (/^paytmqr/i.test(compact)) return true;
    if (/^\d+$/.test(compact)) return true;
    return false;
}

export function matchCandidates(normalizedKey) {
    if (!normalizedKey) return [];
    const tokens = normalizedKey.split(' ').filter(Boolean);
    const out = new Set([normalizedKey]);
    for (let n = Math.min(4, tokens.length); n >= 2; n -= 1) {
        for (let i = 0; i <= tokens.length - n; i += 1) {
            out.add(tokens.slice(i, i + n).join(''));
            out.add(tokens.slice(i, i + n).join(' '));
        }
    }
    if (tokens.length > 1) out.add(tokens.join(''));
    return [...out].sort((a, b) => b.length - a.length);
}

export function hasCommerceHint(normalizedKey) {
    if (!normalizedKey) return false;
    const padded = ` ${normalizedKey} `;
    const compact = normalizedKey.replace(/\s+/g, '');
    return COMMERCE_HINTS.some((hint) => {
        const h = hint.trim().toLowerCase();
        if (h.length < 3) return false;
        if (padded.includes(` ${h} `)) return true;
        const hc = h.replace(/\s+/g, '');
        return hc.length >= 5 && compact.includes(hc);
    });
}

export function looksLikePersonName(normalizedKey) {
    if (!normalizedKey) return false;
    if (isUnnamedQr(normalizedKey)) return false;
    if (hasCommerceHint(normalizedKey)) return false;
    const tokens = normalizedKey.split(' ').filter(Boolean);
    if (tokens.length < 1 || tokens.length > 3) return false;
    if (tokens.some((t) => /\d/.test(t))) return false;
    if (tokens.some((t) => AMBIGUOUS_KEYWORDS.has(t))) return false;
    // Single-token P2P only for clear family nicknames already in taxonomy; else need 2+ tokens
    if (tokens.length === 1) {
        const familyOnly = KEYWORD_INDEX.get(tokens[0]);
        return Boolean(familyOnly && familyOnly.category === 'Family & Friends');
    }
    return tokens.every((t) => t.length >= 2 && t.length <= 18 && /^[a-z]+$/.test(t));
}

function disambiguate(normalizedKey) {
    for (const rule of DISAMBIGUATION_RULES) {
        if (rule.re.test(normalizedKey)) {
            return { category: rule.category, subcategory: rule.subcategory, matchType: 'disambiguation' };
        }
    }
    return null;
}

function canUseKeyword(keyword, normalizedKey) {
    const kw = keyword.toLowerCase().trim();
    // Full-string exact brand (uber, ola, pvr, irctc) — allow len ≥ 3
    if (kw === normalizedKey && kw.length >= 3 && KEYWORD_INDEX.has(kw)) return true;
    if (kw.length >= 6 && !AMBIGUOUS_KEYWORDS.has(kw)) return true;
    if (!AMBIGUOUS_KEYWORDS.has(kw) && kw.length >= 5) return true;
    if (kw.includes(' ')) return true;
    if (hasCommerceHint(normalizedKey)) {
        const without = normalizedKey.replace(new RegExp(`\\b${escapeRegExp(kw)}\\b`, 'g'), ' ').replace(/\s+/g, ' ').trim();
        if (without && hasCommerceHint(without)) return true;
        if (without && without.split(' ').filter(Boolean).length >= 1 && kw.length >= 4) {
            if (AMBIGUOUS_KEYWORDS.has(kw) && without.split(' ').every((t) => !COMMERCE_HINTS.includes(t) && t.length < 4)) {
                return without.length >= 3;
            }
            return !AMBIGUOUS_KEYWORDS.has(kw) || without.split(' ').some((t) => t.length >= 4);
        }
    }
    return false;
}

/**
 * Strong keyword match with disambiguation + gated short tokens.
 */
export function keywordMatch(normalizedKey) {
    if (!normalizedKey) return null;

    const dis = disambiguate(normalizedKey);
    if (dis) return dis;

    const candidates = matchCandidates(normalizedKey);
    for (const c of candidates) {
        if (!KEYWORD_INDEX.has(c)) continue;
        if (!canUseKeyword(c, normalizedKey)) continue;
        return {
            ...KEYWORD_INDEX.get(c),
            matchType: c === normalizedKey ? 'exact' : 'compound',
        };
    }

    const tokens = normalizedKey.split(' ').filter(Boolean);
    for (const token of tokens) {
        if (token.length < 4 || !KEYWORD_INDEX.has(token)) continue;
        if (!canUseKeyword(token, normalizedKey)) continue;
        return { ...KEYWORD_INDEX.get(token), matchType: 'token' };
    }

    const padded = ` ${normalizedKey} `;
    for (const entry of KEYWORD_LIST) {
        const kw = entry.keyword;
        if (kw.length < 5) continue;
        if (!padded.includes(` ${kw} `)) continue;
        if (!canUseKeyword(kw, normalizedKey)) continue;
        return { category: entry.category, subcategory: entry.subcategory, matchType: 'contains' };
    }

    const compact = tokens.join('');
    for (const entry of KEYWORD_LIST) {
        const kwCompact = entry.keyword.replace(/\s+/g, '');
        if (kwCompact.length < 6) continue;
        if (AMBIGUOUS_KEYWORDS.has(entry.keyword) && entry.keyword.length < 6) continue;
        if (compact.includes(kwCompact)) {
            return { category: entry.category, subcategory: entry.subcategory, matchType: 'compact' };
        }
    }

    return null;
}

function matchIncomeNarration(rawLower, type) {
    const isCredit = type && String(type).toUpperCase() === 'CREDIT';
    // Business payouts stay Business — not personal Income
    if (/\bemployee\s+salary\b|\bstaff\s+(salary|payment|payout)\b|\bcontractor\s+payout\b/.test(rawLower)) {
        return null;
    }
    if (/\bcashback\b|\breward\s*credit\b/.test(rawLower)) {
        return { category: 'Income', subcategory: 'Cashback', confidence: 0.9, source: 'income' };
    }
    if (/\brefund\b|\breversal\b/.test(rawLower)) {
        return { category: 'Income', subcategory: 'Refund', confidence: 0.9, source: 'income' };
    }
    if ((isCredit || /\bsalary\s+credit\b/.test(rawLower)) && /\bsalary\b|\bpayroll\b|\bwages\b|\bstipend\b/.test(rawLower)) {
        return { category: 'Income', subcategory: 'Salary', confidence: 0.93, source: 'income' };
    }
    if (isCredit && /\binterest\b|\bdividend\b/.test(rawLower)) {
        return { category: 'Income', subcategory: 'Interest', confidence: 0.88, source: 'income' };
    }
    return null;
}

/**
 * Sync categorizer — no DB / Fuse. Optional txn type for Income heuristics.
 */
export function categorizeMerchantSync(rawMerchant, amount = 0, type = null) {
    const key = normalizeMerchant(rawMerchant);
    const rawLower = String(rawMerchant || '').toLowerCase();

    const income = matchIncomeNarration(rawLower, type);
    if (income) return income;

    if (!key || isUnnamedQr(key)) {
        return { category: 'Uncategorized', subcategory: 'Needs Review', confidence: 0, source: 'none' };
    }

    for (const indicator of SUBSCRIPTION_KEYWORDS) {
        const pattern = new RegExp('\\b' + escapeRegExp(indicator) + '\\b', 'i');
        if (pattern.test(rawLower)) {
            // Don't treat bare phonepe/gpay as subscription — handled by disambiguation / Transfers
            if (/^(phonepe|gpay|google pay|paytm)$/i.test(indicator.trim())) continue;
            return { category: 'Subscription', subcategory: 'Recurring Payment', confidence: 0.95, source: 'subscription' };
        }
    }

    const exact = keywordMatch(key);
    if (exact) {
        const soft = exact.matchType === 'contains' || exact.matchType === 'compact';
        return {
            category: exact.category,
            subcategory: exact.subcategory,
            confidence: soft ? 0.88 : 0.92,
            source: 'keyword',
        };
    }

    const amtNum = parseAmt(amount);
    if (
        amtNum >= 500
        && amtNum <= 99000
        && amtNum % 100 === 0
        && looksLikePersonName(key)
        && !hasCommerceHint(key)
    ) {
        return {
            category: 'Family & Friends',
            subcategory: 'Personal Transfer',
            confidence: 0.7,
            source: 'amount_heuristic',
        };
    }

    return { category: 'Uncategorized', subcategory: 'Needs Review', confidence: 0, source: 'none' };
}

export function getFuseEntries() {
    return KEYWORD_LIST.map(({ keyword, category, subcategory }) => ({ keyword, category, subcategory }));
}

export { escapeRegExp, SUBSCRIPTION_KEYWORDS };
